import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { marked } from 'marked';
import { useEffect } from 'react';
import { updatePageMeta, addStructuredData, generateBlogPostStructuredData } from '../../utils/seo';

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
  sanitize: false,
});

const post = {
  "slug": "diwali-gift-exchange-ideas",
  "title": "Diwali Gift Exchange: Celebrating the Festival of Lights",
  "excerpt": "Discover traditional and modern gift ideas for Diwali celebrations. From sweets to silver, make your Festival of Lights gift exchange memorable.",
  "date": "2024-10-15",
  "readTime": "7 min read",
  "category": "Festivals",
  "image": "🪔",
  "heroImage": "https://images.unsplash.com/photo-1605275990464-81f85bb5bea7?w=1200&h=600&fit=crop",
  "author": "SwapGifts Editorial Team",
  "content": "# Diwali Gift Exchange: Celebrating the Festival of Lights\n\nDiwali, one of the most significant festivals in Hinduism, Jainism, and Sikhism, is a celebration of light over darkness and good over evil. Gift-giving is central to the festivities.\n\n## The Tradition of Gift-Giving\n\nExchanging gifts during Diwali symbolizes sharing prosperity and strengthening relationships. Traditionally, people give:\n\n- **Sweets (Mithai)**: Boxes of traditional Indian sweets\n- **Dry Fruits and Nuts**: Symbol of health and prosperity\n- **Silver Items**: Coins, utensils, or decorative pieces (silver is considered auspicious)\n- **Diyas**: Traditional oil lamps\n\n## Traditional Diwali Gifts\n\n**For Family**:\n- Gold or silver jewelry\n- New clothes (especially for children)\n- Home decor items\n- Lakshmi-Ganesh idols\n\n**For Friends and Colleagues**:\n- Decorative gift hampers\n- Scented candles\n- Tea or coffee gift sets\n- Handicrafts\n\n## Modern Gift Ideas\n\n**Eco-Friendly Options**:\n- Plantable seed crackers\n- Organic skincare products\n- Bamboo or jute home items\n- LED string lights\n\n**Practical Gifts**:\n- Kitchen gadgets\n- Tech accessories\n- Books\n- Subscription boxes\n\n**Experience Gifts**:\n- Spa vouchers\n- Restaurant gift cards\n- Online course subscriptions\n\n## Organizing a Diwali Gift Exchange\n\nFor large families or office celebrations:\n\n**Budget Recommendations**:\n- Colleagues: ₹500-₹1000 ($6-12)\n- Friends: ₹1000-₹2000 ($12-25)\n- Family: ₹2000-₹5000+ ($25-60+)\n\n**Timing**: Dhanteras (2 days before Diwali) through Bhai Dooj (2 days after) are all appropriate for gift-giving.\n\n## Gift-Wrapping Tips\n\n- Use bright colors: red, gold, yellow, or orange\n- Add traditional elements like marigold flowers or rangoli patterns\n- Include a Diwali greeting card with personal wishes\n- Consider reusable cloth wrapping (eco-friendly and traditional)\n\n## What to Avoid\n\n- Leather products (many celebrants are vegetarian/vegan)\n- Black-colored gifts (associated with negativity)\n- Sharp objects like knives (symbolically \"cutting\" relationships)\n- Empty containers (give with something inside)\n\n## Conclusion\n\nDiwali gift exchanges celebrate prosperity, joy, and the bonds we share. Whether you choose traditional sweets or modern gadgets, the spirit of giving illuminates the festival."
};

export default function DiwaliGiftExchangeIdeasPage() {
  useEffect(() => {
    updatePageMeta({
      title: `${post.title} | SwapGifts Blog`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogImage: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
      canonicalUrl: `https://swapgifts.store/blog/${post.slug}`,
      keywords: `gift exchange, ${post.category.toLowerCase()}, secret santa, ${post.title.toLowerCase()}`
    });

    addStructuredData(generateBlogPostStructuredData({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      author: post.author || 'SwapGifts Editorial Team',
      heroImage: post.heroImage,
      slug: post.slug
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-green-600 font-medium hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </a>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {post.heroImage && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>

            {post.author && (
              <p className="text-sm text-gray-500 mb-6">
                By <span className="font-medium text-gray-700">{post.author}</span>
              </p>
            )}

            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-b border-gray-200 pb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700 prose-li:leading-relaxed
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(post.content) }}
            />
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to organize your gift exchange?</h2>
            <p className="text-green-100 mb-6">
              Put these tips into action and create your exchange in minutes
            </p>
            <a
              href="/create"
              className="inline-block px-8 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Drawing Names
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
